import { useMemo, useState } from 'react';
import { nip19 } from 'nostr-tools';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useBCHPrice } from '@/hooks/useBCHPrice';
import { useBusinessVerification } from '@/hooks/useBusinessVerification';
import {
  PLATFORM_FEE_ADDRESS,
  DEFAULT_BUSINESS_VERIFY_FEE_USD,
  BUSINESS_VERIFY_PERIOD_DAYS,
  verifyFeeSats,
  buildVerifyClaimEvent,
  auditClaim,
} from '@/lib/businessVerify';
import { toast } from '@/hooks/useToast';
import {
  ShieldCheck,
  ShieldAlert,
  Copy,
  Check,
  Loader2,
  CircleCheck,
  Clock,
} from 'lucide-react';

interface BchnostrVerifyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TXID_RE = /^[0-9a-f]{64}$/i;

/** Build a BIP21 bitcoincash: URI with an amount in BCH. */
function bip21(address: string, bchAmount: number): string {
  const addr = address.replace(/^bitcoincash:/i, '');
  const amount = bchAmount > 0
    ? `?amount=${bchAmount.toFixed(8).replace(/0+$/, '').replace(/\.$/, '')}`
    : '';
  return `bitcoincash:${addr}${amount}`;
}

export function BchnostrVerifyDialog({ open, onOpenChange }: BchnostrVerifyDialogProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { data: price, isLoading: priceLoading } = useBCHPrice();
  const verification = useBusinessVerification(user?.pubkey);

  const [txid, setTxid] = useState('');
  const [copied, setCopied] = useState<'address' | 'uri' | null>(null);
  const [phase, setPhase] = useState<'idle' | 'auditing' | 'publishing'>('idle');
  const [error, setError] = useState('');

  const feeUsd = DEFAULT_BUSINESS_VERIFY_FEE_USD;
  const feeSats = useMemo(() => verifyFeeSats(feeUsd, price ?? null), [feeUsd, price]);
  const bchAmount = feeSats / 1e8;
  const uri = useMemo(() => bip21(PLATFORM_FEE_ADDRESS, bchAmount), [bchAmount]);
  const bareAddress = PLATFORM_FEE_ADDRESS.replace(/^bitcoincash:/i, '');

  const npub = user ? nip19.npubEncode(user.pubkey) : '';
  const status = verification.data?.status ?? 'UNVERIFIED';
  const busy = phase !== 'idle';

  const copy = async (text: string, which: 'address' | 'uri') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast({ title: 'Copy failed', description: 'Copy the value manually.', variant: 'destructive' });
    }
  };

  const handleSubmit = async () => {
    setError('');
    const clean = txid.trim().toLowerCase();
    if (!TXID_RE.test(clean)) {
      setError('Enter a valid 64-character transaction ID (txid).');
      return;
    }

    try {
      // 1) On-chain audit — give instant feedback before publishing.
      setPhase('auditing');
      const audit = await auditClaim(clean, feeSats);
      if (audit.verdict === 'wrong-recipient') {
        throw new Error('That transaction did not pay the verification address.');
      }
      if (audit.verdict === 'underpaid') {
        throw new Error('That payment was less than the verification fee.');
      }
      if (audit.verdict === 'not-found' || audit.verdict === 'no-record') {
        throw new Error('Could not find that transaction yet. Wait for it to broadcast, then try again.');
      }

      // 2) Publish the signed claim event from the (business) signer.
      setPhase('publishing');
      const { kind, tags, content } = buildVerifyClaimEvent(clean, feeSats);
      await publishEvent({ kind, tags, content });

      toast({
        title: 'Claim submitted',
        description: 'Your payment claim was published. An issuer will review and grant your badge.',
      });
      setTxid('');
      verification.refetch();
      setPhase('idle');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
      setPhase('idle');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-bch" />
            Get Verified on BCHNostr
          </DialogTitle>
          <DialogDescription>
            Pay the one-time ${feeUsd} fee in BCH, then submit your transaction to claim a
            verified badge for {BUSINESS_VERIFY_PERIOD_DAYS} days.
          </DialogDescription>
        </DialogHeader>

        {/* Business npub warning — always visible */}
        <Alert variant="destructive" className="border-amber-500/40 bg-amber-500/5 text-foreground">
          <ShieldAlert className="size-4 text-amber-600 dark:text-amber-400" />
          <AlertTitle className="text-amber-700 dark:text-amber-400">
            Log in with your BUSINESS npub
          </AlertTitle>
          <AlertDescription className="text-muted-foreground">
            Verification is tied to the Nostr key that published your business listing —
            <strong className="text-foreground"> not your personal account</strong>. Make sure
            you are logged in with your <strong className="text-foreground">business npub</strong>{' '}
            before submitting, or the badge will attach to the wrong profile.
          </AlertDescription>
        </Alert>

        {!user ? (
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground text-center">
              Log in with your business account to continue.
            </p>
            <LoginArea className="w-full" />
          </div>
        ) : status === 'VERIFIED' ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <CircleCheck className="size-10 text-bch" />
            <p className="font-semibold">This business is already verified 🎉</p>
            {verification.data?.expiresAt ? (
              <p className="text-xs text-muted-foreground">
                Valid until {new Date(verification.data.expiresAt * 1000).toLocaleDateString()}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Logged-in identity */}
            <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs">
              <span className="text-muted-foreground">Submitting as: </span>
              <span className="font-mono">{npub.slice(0, 16)}…{npub.slice(-6)}</span>
            </div>

            {status === 'PENDING' && (
              <Alert className="border-bch/30 bg-bch/5">
                <Clock className="size-4 text-bch" />
                <AlertTitle>Claim pending review</AlertTitle>
                <AlertDescription className="text-muted-foreground">
                  You've already submitted a payment claim. An issuer will grant your badge shortly.
                  You can re-submit below if needed.
                </AlertDescription>
              </Alert>
            )}

            {/* Fee + payment */}
            <div className="flex flex-col items-center gap-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-bch">${feeUsd}</div>
                <div className="text-xs text-muted-foreground">
                  {priceLoading || feeSats <= 0
                    ? 'Fetching BCH price…'
                    : `≈ ${bchAmount.toFixed(8).replace(/0+$/, '').replace(/\.$/, '')} BCH · one-time`}
                </div>
              </div>

              <div className="rounded-xl bg-white p-3">
                <QRCodeCanvas value={uri} size={180} />
              </div>

              <div className="w-full space-y-2">
                <div className="flex items-center gap-2">
                  <Input readOnly value={bareAddress} className="font-mono text-xs" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => copy(bareAddress, 'address')}
                    aria-label="Copy address"
                  >
                    {copied === 'address' ? <Check className="size-4 text-bch" /> : <Copy className="size-4" />}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={() => copy(uri, 'uri')}
                  disabled={feeSats <= 0}
                >
                  {copied === 'uri' ? <Check className="size-4 mr-2 text-bch" /> : <Copy className="size-4 mr-2" />}
                  Copy payment link
                </Button>
              </div>
            </div>

            <Separator />

            {/* Submit txid */}
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="txid-input">
                After paying, paste your transaction ID
              </label>
              <Input
                id="txid-input"
                placeholder="64-character txid"
                value={txid}
                onChange={(e) => setTxid(e.target.value)}
                className="font-mono text-xs"
                disabled={busy}
              />
              {error && <p className="text-xs text-destructive">{error}</p>}
              <Button
                type="button"
                className="w-full bg-bch hover:bg-bch/90 text-bch-foreground"
                onClick={handleSubmit}
                disabled={busy || !txid.trim()}
              >
                {phase === 'auditing' && <Loader2 className="size-4 mr-2 animate-spin" />}
                {phase === 'publishing' && <Loader2 className="size-4 mr-2 animate-spin" />}
                {phase === 'auditing'
                  ? 'Checking payment…'
                  : phase === 'publishing'
                    ? 'Publishing claim…'
                    : 'Submit verification claim'}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                We confirm your payment on-chain, then publish a signed claim from your account.
                An issuer reviews it and grants the badge.
              </p>
            </div>

            <div className="flex justify-center">
              <Badge variant="outline" className="text-[10px] font-mono">
                kind 30078 · valid {BUSINESS_VERIFY_PERIOD_DAYS}d
              </Badge>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
